/**
 * Slot assignment — Dim / On / Bright (SPEC.md §6.2, D182, D211).
 *
 * `ai_enabled` shown here is read-only: it's `EFSS_AI_ENABLED`, a container-level
 * deployment choice read once at boot (`GET /api/health`), not a per-session toggle. This
 * card displays it and explains how to change it; nothing here writes it.
 *
 * **Each picker only offers what this account may actually assign** (D211's eligibility
 * rule: their own connections, plus the admin's credential-free ones) — `configs` from
 * `useModelConfigs` already includes the admin's connections wholesale (for visibility in
 * `ModelConfigsCard`'s own list), so it isn't the eligible set by itself; filtered here
 * against `me` and `has_api_key` rather than offering an option the server would 404 on
 * save (reported directly: the dropdown "still listed every connection"). A slot's current
 * *effective* pick (`GET /api/models/slots`, which can be the admin's own fallback) can
 * still be one this account can't reassign to — shown as a disabled option instead of
 * silently vanishing, so the picker never looks like "none" when something is actually
 * answering.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type Health, type Me, type ModelSlots } from "../api";
import { resetAiConfigured } from "../aiConfigured";
import { useModelConfigs } from "../modelConfigs";
import { Lightbulb } from "./icons";

const SLOTS: { id: keyof ModelSlots; label: string; blurb: string }[] = [
  { id: "dim", label: "Dim", blurb: "Fast, always available, rougher" },
  { id: "on", label: "On", blurb: "The default. Better, may need the GPU" },
  { id: "bright", label: "Bright", blurb: "Best. Slowest or costliest, worth it when it matters" },
];

const GPU_MODE_BLURB: Record<string, string> = {
  free: "full budget, anything can run",
  shared: "small models only, the rest is streaming's",
  off: "nothing local right now, queued until it frees up",
};

export function ModelSlotsCard({
  refreshKey,
  onChange,
}: {
  refreshKey?: number;
  onChange?: () => void | Promise<void>;
}) {
  const { configs } = useModelConfigs(refreshKey);
  const [health, setHealth] = useState<Health | null>(null);
  const [slots, setSlots] = useState<ModelSlots | null>(null);
  const [selection, setSelection] = useState<Record<keyof ModelSlots, string>>({
    dim: "",
    on: "",
    bright: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [me, setMe] = useState<Me | null>(null);

  const load = useCallback(async () => {
    try {
      const [h, s] = await Promise.all([api.health(), api.modelSlots()]);
      setHealth(h);
      setSlots(s);
      setSelection({
        dim: s.dim?.id ?? "",
        on: s.on?.id ?? "",
        bright: s.bright?.id ?? "",
      });
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return;
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    void api.me().then(setMe);
  }, []);

  // Own connections (any) plus the admin's credential-free ones — the eligibility rule
  // `services/model_assignment.py::is_assignable` enforces server-side, mirrored here so
  // the picker never offers something the save would reject.
  const eligible = configs.filter(
    (config) => me != null && (config.created_by === me.id || !config.has_api_key),
  );

  // A slot's `selection` value can be the disabled "not yours" option (D211's admin
  // fallback, kept in `selection` purely so the picker can render it) — never the
  // account's own explicit pick, since eligible ids are the only ones the `<select>` lets
  // anyone actually choose. Submitting it as-is would 404 the whole PUT; `null` is the
  // correct send instead, and always a no-op here, since an ineligible current value can
  // only mean "nothing of mine is set for this slot" in the first place.
  function toSubmit(slotId: keyof ModelSlots): string | null {
    const value = selection[slotId];
    if (!value) return null;
    return eligible.some((config) => config.id === value) ? value : null;
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    try {
      await api.setModelSlots({
        dim: toSubmit("dim"),
        on: toSubmit("on"),
        bright: toSubmit("bright"),
      });
      resetAiConfigured(); // the bulbs elsewhere ask again: this may be what gave a fresh server AI
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <section className="panel">
      <header>
        <h2>Model assignment</h2>
        <span className="muted small">Effort, not a model - the picker stays the same</span>
      </header>

      {health && (
        <p className={`small ${health.ai_enabled ? "muted" : ""}`}>
          {health.ai_enabled ? (
            "AI features are on for this instance."
          ) : (
            <>
              <strong>AI features are off.</strong> That's{" "}
              <code>EFSS_AI_ENABLED</code> in the container environment, not a switch on this
              page - set it to <code>true</code> and restart to turn them on.
            </>
          )}
        </p>
      )}

      {health && (
        <p className="small muted">
          GPU: <strong>{health.gpu_mode}</strong> - {GPU_MODE_BLURB[health.gpu_mode] ?? "unknown"}.
          Set by colossus (gaming/streaming take priority), not a switch here.
        </p>
      )}

      {error && <p className="error">{error}</p>}

      {eligible.length === 0 ? (
        <p className="muted small">Add a model connection first - there's nothing to assign yet.</p>
      ) : (
        <form onSubmit={save} style={{ display: "grid", gap: "0.6rem" }}>
          {SLOTS.map((slot) => {
            const current = selection[slot.id];
            const currentIsEligible = eligible.some((config) => config.id === current);
            return (
              <label key={slot.id} className="row" title={slot.blurb}>
                <span className="row" style={{ minWidth: "4rem", gap: "0.35rem" }}>
                  <Lightbulb level={slot.id} size={14} />
                  {slot.label}
                </span>
                <select
                  className="input"
                  aria-label={slot.label}
                  value={selection[slot.id]}
                  onChange={(e) =>
                    setSelection((s) => ({ ...s, [slot.id]: e.target.value }))
                  }
                >
                  <option value="">- none -</option>
                  {/* Whatever's actually in effect (GET /models/slots, D211's admin
                      fallback) but not this account's to reassign - disabled rather than
                      omitted, so picking something else doesn't read as "it was already
                      off." */}
                  {current && !currentIsEligible && (
                    <option value={current} disabled>
                      {slots?.[slot.id]?.name ?? "Currently assigned"} (not yours to change)
                    </option>
                  )}
                  {eligible.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.name}
                    </option>
                  ))}
                </select>
              </label>
            );
          })}

          <div className="row wrap">
            <button className="btn primary" type="submit">
              Save assignment
            </button>
            {saved && <span className="small" style={{ color: "var(--good)" }}>Saved</span>}
          </div>
        </form>
      )}
    </section>
  );
}
